<template>
  <div class="container">
    <div :class="widthClass">
      <alert-message
        v-if="isInvalid"
        :text="$t('core_medicalofficesearch_searcherror_title')"
        error
      />
      <sanitas-medical-office-form
        :disable-drop-down="disableDropDown"
        :payload="localPayload"
        :networks="networks"
        :ranges="ranges"
        :variant="variant"
        :hide-cancel="hideCancel"
        :is-invalid="isInvalid"
        @submit="handleSubmit"
      ></sanitas-medical-office-form>
    </div>
    <div class="container__row-full">
      <div v-if="loading" class="medical_office_results_loading">
        <Spinner class="theme--green" />
      </div>
      <sanitas-medical-office-results
        v-else
        :search-executed="searchExecuted"
        :results="resultsFormatted"
      >
      </sanitas-medical-office-results>
    </div>
  </div>
</template>

<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'
import { addMonths, format, startOfMonth } from 'date-fns'

import { client } from '@/helpers/http'
import { setTrackerVersion, trackAction, tracker } from '@/helpers/tracker'

import AlertMessage from './components/AlertMessage'
import SanitasMedicalOfficeForm from './components/SanitasMedicalOfficeForm'
import SanitasMedicalOfficeResults from './components/SanitasMedicalOfficeResults'

export default {
  name: 'MedicalOfficeSearch',
  components: {
    AlertMessage,
    SanitasMedicalOfficeForm,
    SanitasMedicalOfficeResults,
    Spinner: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/Spinner'
      )
    },
  },
  props: {
    isFullWidth: Boolean,
    disableDropDown: Boolean,
    hideCancel: Boolean,

    payload: {
      type: Object,
      default: () => ({}),
    },

    variant: {
      type: Number,
      default: 2,
    },

    ranges: {
      type: Array,
      default: () => [
        { label: 'core_medicalofficesearch_place_title', value: 0 },
        {
          label: 'core_medicalofficesearch_place_distance_five_title',
          value: 5,
        },
        {
          label: 'core_medicalofficesearch_place_distance_ten_title',
          value: 10,
        },
      ],
    },
  },

  setup() {
    const language = window.Sanitas.language || 'de'
    const loadLabels = lang => import(`./i18n/${lang}.json`)
    provideI36n(language, { load: loadLabels })
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },

  data: () => ({
    isInvalid: false,
    searchResults: [],
    searchExecuted: false,
    localPayload: {
      date: undefined,
      zip: undefined,
      radius: 0,
      doc: undefined,
      doc_surname: undefined,
      network: '',
    },
    loading: false,
  }),
  computed: {
    widthClass() {
      if (this.isFullWidth) {
        return 'container__row-full'
      } else {
        return 'container__row-two-third'
      }
    },
    networks() {
      return [
        {
          name: this.$t('core_medicalofficesearch_product_CAREMED_title'),
          value: 'CAREMED',
        },
        {
          name: this.$t('core_medicalofficesearch_product_NETMED_title'),
          value: 'NETMED',
        },
        {
          name: this.$t('core_medicalofficesearch_product_MEDBASE_title'),
          value: 'MEDBASE',
        },
      ]
    },
    resultsFormatted() {
      return this.searchResults.reduce(function (result, item) {
        let medicalNets = item.medicalOfficeNets
        if (medicalNets.length > 0) {
          const sortedBy = {
            CAREMED: 0,
            NETMED: 1,
            MEDBASE: 2,
          }
          medicalNets.sort(
            (a, b) => sortedBy[a.avmDefinition] - sortedBy[b.avmDefinition],
          )
          const networks = medicalNets
            .filter(function (network) {
              return (
                network.avmDefinition === 'MEDBASE' ||
                network.avmDefinition === 'NETMED'
              )
            })
            .map(function (network) {
              return network.description
            })
          result.push({
            zsrNumber: item.zsrNumber,
            officeState: item.medicalOfficeStatus,
            name: item.firstName,
            surname: item.lastName,
            address: ''
              .concat(item.street)
              .concat(' ')
              .concat(item.houseNumber),
            plz: item.zipCode,
            region: item.town,
            phone: item.phoneNumber,
            networks: networks,
            medicalOfficeNets: medicalNets,
          })
        }
        return result
      }, [])
    },
  },

  created() {
    setTrackerVersion()

    if (this.payload) {
      this.localPayload = this.payload
    }
  },
  methods: {
    handleSubmit() {
      let isInvalid = true
      // caremed in mind -> remove the network
      if (this.variant === 0) {
        delete this.localPayload.network
      }
      let keys = Object.keys(this.localPayload)

      for (let i = 0; i < keys.length; i++) {
        if (
          keys[i] !== 'radius' &&
          keys[i] !== 'network' &&
          this.localPayload[keys[i]] !== undefined &&
          this.localPayload[keys[i]] !== ''
        ) {
          isInvalid = false
          break
        }
      }

      if (isInvalid && this.localPayload.network === 'MEDBASE') {
        // only for MEDBASE: allow search without further criteria
        isInvalid = false
      }

      this.isInvalid = isInvalid
      if (!isInvalid) {
        const { date, zip, radius, doc, doc_surname, network } =
          this.localPayload
        const searchParams = {
          zsrNumber: '',
          lastName: doc ? doc : '',
          firstName: doc_surname ? doc_surname : '',
          zipCode: zip ? zip.zipCode : '',
          town: zip ? zip.town : '',
          avmNetwork: [network],
          contractStartDat: format(
            startOfMonth(addMonths(new Date(), 1)),
            'yyyy-MM-dd',
          ),
          limit: 300,
        }
        if (network === undefined || network === '' || network === false) {
          delete searchParams.avmNetwork
          //searchParams.avmNetwork = ['CAREMED','NETMED','MEDBASE'];
        }
        if (radius) {
          searchParams.radius = radius
        }
        this.doSearch(searchParams)
      } else {
        this.searchResults = []
      }
    },
    doSearch(params) {
      this.loading = true
      client('api')
        .post('masterdata', 'careprovider-search', params)
        .then(({ data }) => {
          this.searchExecuted = true
          this.searchResults = data
          this.loading = false

          tracker.set({
            data: {
              search: {
                formId: 'doctor',
                query: '',
                resultsCount: this.searchResults.length,
              },
            },
          })

          if (this.searchResults.length > 0) {
            trackAction('ComSearchSubmitted')
          } else {
            trackAction('ComSearchSubmittedNoResult')
          }
        })
        .catch(err => {
          this.searchExecuted = true
          this.searchResults = []
          this.loading = false
          tracker.set({
            data: { search: { formId: 'doctor', query: '', resultsCount: 0 } },
          })
          trackAction('ComSearchSubmittedNoResult')
        })
    },
  },
}
</script>

<style lang="postcss" scoped>
.container {
  display: flex;
  flex-direction: column;

  &__row {
    &-two-third {
      width: 100%;
    }

    &-full {
      width: 100%;
    }
  }
}

.medical_office_results_loading {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 20px;
}

@media (--md) {
  .container {
    &__row {
      &-two-third {
        width: 66%;
      }
    }
  }
}
</style>
